'use client'

import { useRef, useState, type ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

/**
 * Hover-to-preview (desktop) + tap-to-pin (desktop and mobile) disclosure for a short info
 * explainer or an exact figure behind a compact/abbreviated display. Radix's `Tooltip` has no
 * tap-to-open mode (a tap opens then immediately closes on touch devices), so this wraps the
 * tap-friendly `Popover` with manually controlled open state instead.
 *
 * Hovering opens it and closes it again on mouse-leave, matching a normal tooltip's feel. Clicking
 * or tapping it "pins" it open until an outside click/tap/Escape closes it (Radix's own
 * `onOpenChange(false)` already fires for that) — a plain `open` boolean can't tell these two states
 * apart, so a separate `pinned` ref tracks it; otherwise moving the mouse away would also close a
 * popover the user deliberately clicked open.
 *
 * The `onClick` below calls `preventDefault()` deliberately: `PopoverTrigger`'s own `onClick` is
 * `composeEventHandlers(props.onClick, context.onOpenToggle)` (confirmed in
 * `@radix-ui/react-popover`'s source) — without suppressing it, every click would *also* toggle
 * Radix's own open state right after ours runs, which would immediately re-close a popover that was
 * already open via hover before the click (since "toggle" flips whatever `open` currently is).
 * `preventDefault()` makes `composeEventHandlers` skip that second call, leaving our own `setOpen`
 * call as the only thing driving state.
 */
export function InfoPopover({
  trigger,
  children,
  contentClassName,
}: {
  trigger: ReactNode
  children: ReactNode
  contentClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const pinned = useRef(false)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) pinned.current = false
        setOpen(next)
      }}
    >
      <PopoverTrigger
        asChild
        onMouseEnter={() => {
          if (!pinned.current) setOpen(true)
        }}
        onMouseLeave={() => {
          if (!pinned.current) setOpen(false)
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          pinned.current = true
          setOpen(true)
        }}
      >
        {trigger}
      </PopoverTrigger>
      <PopoverContent className={contentClassName}>{children}</PopoverContent>
    </Popover>
  )
}
